import { z } from 'zod'
import { FreeTextCitedFactSchema, StructuredCitedFactSchema } from './citedFact'

const supplierName = z.string().describe('The supplier name, exactly as the RFx context lists it for the round.')

const RfxAnswerTargetSchema = z.object({
  kind: z.literal('answer'),
  supplier: supplierName,
  questionnaire: z.string().describe('The questionnaire name, as readRfxSupplierResponses heads it.'),
  question: z.string().describe('The question, exactly as it follows "Q:" in readRfxSupplierResponses.'),
})

const RfxScoreTargetSchema = z.object({
  kind: z.literal('score'),
  criterion: z.string().nullable().describe('The criterion label, as readRfxScores lists it; null for the overall score.'),
  supplier: supplierName,
})

const RfxQnaTargetSchema = z.object({
  kind: z.literal('qna'),
  question: z.string().describe('The published question, exactly as it follows "Q:" in readRfxQandA.'),
})

const RFX_DECISION_PARTS = ['outcome', 'awarded_suppliers', 'award_lanes', 'award_recommendation'] as const

const RfxDecisionTargetSchema = z.object({
  kind: z.literal('decision'),
  part: z.enum(RFX_DECISION_PARTS).describe("Which part of readRfxDecision's output is cited."),
})

export const RFX_COMMENT_GROUPS = ['INTERNAL', 'ANNOUNCEMENT', 'SUPPLIER'] as const

const RfxCommentTargetSchema = z.object({
  kind: z.literal('comment'),
  group: z.enum(RFX_COMMENT_GROUPS).describe('The readRfxComments group the comment is in.'),
  supplier: supplierName.nullable().describe('The supplier whose messages these are; null unless group is SUPPLIER.'),
})

const RfxSupplierOutcomeTargetSchema = z.object({
  kind: z.literal('supplier_outcome'),
  supplier: supplierName,
})

// Plain `z.union` of objects for the same OpenAI strict-mode reason as the cross-slice unions.
export const RfxCitationTargetSchema = z.union([
  RfxAnswerTargetSchema,
  RfxScoreTargetSchema,
  RfxQnaTargetSchema,
  RfxDecisionTargetSchema,
  RfxCommentTargetSchema,
  RfxSupplierOutcomeTargetSchema,
])
export type RfxCitationTarget = z.infer<typeof RfxCitationTargetSchema>

export const RfxCitationAgentInputSchema = z.object({
  type: z.literal('rfx'),
  round: z.number().int().describe('The round the cited fact is from, as the RFx context numbers it.'),
  target: RfxCitationTargetSchema.describe(
    'What is cited: a supplier answer, a score, a published Q&A, the award decision, a comment, or a supplier status.'
  ),
  anchor: z
    .string()
    .trim()
    .min(1)
    .nullable()
    .describe(
      "Verbatim phrase from the cited text, or null. Required for an answer, a Q&A, a comment, a score's rationale, a supplier's rejection or withdrawal comment and the award recommendation; leave it null for a score, status, outcome, awarded suppliers or award lanes."
    ),
})

export const RfxFactAspectSchema = z.enum(['answer', 'score', 'qna', 'comment', 'supplier_outcome', ...RFX_DECISION_PARTS])
export type RfxFactAspect = z.infer<typeof RfxFactAspectSchema>

// `subject` names the cited thing within the card (a question, a criterion, a comment thread); null for an
// aspect that names itself, like the outcome.
const rfxFactFields = { aspect: RfxFactAspectSchema, subject: z.string().nullable() }

export const CitedRfxFactSchema = z.discriminatedUnion('kind', [
  z.strictObject({ ...StructuredCitedFactSchema.shape, ...rfxFactFields }),
  z.strictObject({ ...FreeTextCitedFactSchema.shape, ...rfxFactFields }),
])
export type CitedRfxFact = z.infer<typeof CitedRfxFactSchema>

const RfxCommentThreadSchema = z.union([
  z.object({ group: z.literal('SUPPLIER'), rfxSupplierId: z.string() }),
  z.object({ group: z.enum(['INTERNAL', 'ANNOUNCEMENT']) }),
])
export type RfxCommentThread = z.infer<typeof RfxCommentThreadSchema>

export const RfxCitationLinkSchema = z.union([
  z.object({ page: z.literal('question'), requestFormTemplateId: z.string(), questionName: z.string() }),
  z.object({ page: z.literal('comments'), thread: RfxCommentThreadSchema }),
  z.object({ page: z.enum(['results', 'qna', 'overview']) }),
])
export type RfxCitationLink = z.infer<typeof RfxCitationLinkSchema>

export const RfxSourceTitleSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('answer'), supplier: z.string(), questionnaire: z.string(), question: z.string() }),
  z.object({ kind: z.literal('scores'), supplier: z.string() }),
  z.object({ kind: z.literal('rationale'), supplier: z.string(), criterion: z.string().nullable() }),
  z.object({ kind: z.literal('qna'), question: z.string() }),
  z.object({ kind: z.literal('outcome') }),
  z.object({ kind: z.literal('award_recommendation') }),
  z.object({ kind: z.literal('internal_comments') }),
  z.object({ kind: z.literal('announcements') }),
  z.object({ kind: z.literal('supplier_messages'), supplier: z.string() }),
  z.object({ kind: z.literal('status'), supplier: z.string() }),
  z.object({ kind: z.literal('status_comment'), supplier: z.string() }),
])
export type RfxSourceTitle = z.infer<typeof RfxSourceTitleSchema>

// One card per record its link opens. A quote's card is the text it came from (an answer, a thread, a
// rationale); scores, statuses and the outcome stack on their supplier's card, or the round's.
export const RfxCitationResolvedSchema = z.object({
  sourceType: z.literal('rfx'),
  requestId: z.string(),
  round: z.number().int(),
  rfxSupplierId: z.string().nullable(),
  roundCount: z.number().int(),
  sourceTitle: RfxSourceTitleSchema,
  link: RfxCitationLinkSchema,
  citedFacts: z.array(CitedRfxFactSchema).min(1),
})
export type RfxCitationResolved = z.infer<typeof RfxCitationResolvedSchema>
